import React, { useEffect, ReactNode } from 'react';
import { App } from 'antd';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearCredentials, updateSubscriptionInfo } from '../../store/slices/authSlice';
import { setCurrentRestaurant } from '../../store/slices/restaurantSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTokenRefresh } from '../../hooks/useTokenRefresh';
import { useGetCurrentUserQuery } from '../../services/baseApi';
import { decodeJwt } from '../../utils/jwt';
import { isUnreachableError, useConnectionStatus } from '../../services/connectionStatus';
import { useGetSubscriptionStatusQuery } from '../../services/stripeApi';
import { useGetUserLimitInfoQuery } from '../../services/usersApi';

interface AuthProviderProps {
  children: ReactNode;
}

const PUBLIC_AUTH_PATHS = ['/login', '/register', '/auth/login', '/auth/register'];

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken, refreshToken, isAuthenticated, error, user, plan } = useAppSelector(state => state.auth);
  const authPaths = PUBLIC_AUTH_PATHS;

  // Fetch subscription status from Stripe to sync with backend state
  const { data: subscriptionStatus } = useGetSubscriptionStatusQuery(undefined, {
    skip: !isAuthenticated || !accessToken,
  });

  // Fetch user limit info for additional subscription data
  const { data: limitInfo } = useGetUserLimitInfoQuery(undefined, {
    skip: !isAuthenticated || !accessToken,
  });

  // Sync subscription status with Redux state
  useEffect(() => {
    if (subscriptionStatus && limitInfo && isAuthenticated) {
      const stripePlanName = subscriptionStatus.planName;
      const stripeStatus = subscriptionStatus.status;

      // Determine if subscription is active based on Stripe status (any plan)
      const isStripeActive = stripeStatus === 'active' || stripeStatus === 'trialing';
      const hasSubscription = subscriptionStatus.hasSubscription;

      // Check if trial period is still valid (from database TrialEndDate)
      const trialEndDateValue = subscriptionStatus.trialEnd || limitInfo.trialEndDate;
      const isTrialStillValid = trialEndDateValue && new Date(trialEndDateValue) > new Date();

      // isTrialExpired is only true if:
      // 1. No active subscription in Stripe AND
      // 2. Trial period has expired (trialEndDate is in the past)
      const isTrialExpiredNow = !isStripeActive && !hasSubscription && !isTrialStillValid && limitInfo.isTrialExpired;

      const currentPlan = plan;
      const needsUpdate = currentPlan !== stripePlanName ||
                        plan !== stripePlanName;

      if (needsUpdate || (isStripeActive && stripePlanName)) {
        dispatch(updateSubscriptionInfo({
          plan: stripePlanName || 'trial',
          isTrialExpired: isTrialExpiredNow,
          maxUsers: limitInfo.maxUsers,
          trialEndDate: trialEndDateValue,
        }));
      }
    }
  }, [subscriptionStatus, limitInfo, isAuthenticated, plan, dispatch]);

  // Fetch current user data using RTK Query hook
  const shouldSkip = !accessToken || !refreshToken || (isAuthenticated && !!user);
  const { data: currentUser, isLoading: isUserLoading, isError: isUserError, error: currentUserError, refetch: refetchCurrentUser } = useGetCurrentUserQuery(undefined, {
    skip: shouldSkip, // Skip if no tokens or user already loaded
  });

  // Si /auth/me falló por falta de red, la sesión se conserva (ver abajo) pero la app
  // queda sin usuario ni rol. Nada vuelve a pedirlo solo —la app no usa
  // refetchOnReconnect—, así que se pide aquí en cuanto regresa la conexión.
  const connection = useConnectionStatus();
  useEffect(() => {
    if (connection === 'online' && !shouldSkip && isUserError
        && isUnreachableError(currentUserError as { status: unknown } | undefined)) {
      refetchCurrentUser();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection]);

  // Initialize persistent restaurant session management
  useTokenRefresh();

  useEffect(() => {
    // If user data is successfully fetched, update Redux store with role from JWT
    if (currentUser && !user && accessToken) {
      const claims = decodeJwt(accessToken);
      const userWithRole = claims?.role
        ? { ...currentUser, role: claims.role }
        : currentUser;

      dispatch({
        type: 'auth/setCredentials',
        payload: {
          accessToken,
          refreshToken: refreshToken || '',
          user: userWithRole
        }
      });
    }
  }, [currentUser, user, accessToken, refreshToken, dispatch]);

  // Initialize restaurant from JWT token and update user role
  useEffect(() => {
    if (accessToken && isAuthenticated) {
      const claims = decodeJwt(accessToken);
      if (claims) {
        // Initialize restaurant if available
        if (claims.restaurantId) {
          dispatch(setCurrentRestaurant({
            id: claims.restaurantId,
            name: claims.restaurantName || 'Restaurant',
            organizationId: claims.organizationId,
            isActive: true,
            createdAt: new Date().toISOString(),
          }));
        }

        // Update user with role from JWT if user exists but role is missing
        if (user && !user.role && claims.role) {
          dispatch({
            type: 'auth/setCredentials',
            payload: {
              accessToken,
              refreshToken: refreshToken || '',
              user: { ...user, role: claims.role }
            }
          });
        }
      }
    }
  }, [accessToken, isAuthenticated, user, refreshToken, dispatch]);

  useEffect(() => {
    // Handle authentication errors from RTK Query or Redux store
    // Only handle errors if we're not currently loading (to avoid race conditions on refresh)
    // IMPORTANT: Only clear credentials for 401/403 errors, not server errors (500)
    if (!isUserLoading && (isUserError || (error && error.includes('expirada')))) {
      console.error('🔴 Authentication error:', { isUserError, error });

      // Sin red (o Render caído) /auth/me no dice nada de la sesión: no se llegó al
      // servidor. Antes esto borraba los tokens y mandaba al login, así que abrir la
      // app con el internet caído deslogueaba a la cajera y ya no podía volver a
      // entrar hasta que regresara la red. Se conserva la sesión; el aviso de
      // conexión ya dice lo que pasa, y el efecto de arriba repite la consulta al
      // volver la conexión.
      if (isUserError && isUnreachableError(currentUserError as { status: unknown } | undefined)) {
        console.warn('⚠️ /auth/me sin respuesta del servidor - se conserva la sesión');
        return;
      }

      // Check if this is a server error (5xx) vs authentication error (401/403)
      const isServerError = error && (error.includes('500') || error.includes('server error') || error.includes('Internal Server Error'));

      if (isServerError) {
        console.warn('⚠️ Server error detected - keeping session active, will retry');
        message.error('Error del servidor. Reintentando...');
        // Don't clear credentials for server errors - keep the session
        return;
      }

      // Only clear credentials for actual auth errors (401, 403, expired tokens)
      console.warn('🔐 Authentication failure detected - clearing credentials');
      message.error(error || 'Sesión expirada. Por favor, inicia sesión nuevamente.');
      dispatch(clearCredentials());

      // Redirect to login if not already there or on register page
      if (!authPaths.includes(location.pathname)) {
        navigate('/login', { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserError, error, navigate, location.pathname, dispatch, isUserLoading]);

  useEffect(() => {
    // Redirect to login if not authenticated and not on login or register page
    // Don't redirect while loading user data after refresh or if we have valid tokens
    const hasValidTokens = accessToken && refreshToken;
    const shouldRedirect = !isAuthenticated && !hasValidTokens && !authPaths.includes(location.pathname) && !isUserLoading;

    if (shouldRedirect) {
      navigate('/login', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken, refreshToken, navigate, location.pathname, isUserLoading]);

  return <>{children}</>;
};

export default AuthProvider;

